import { useState, useEffect, useCallback } from 'react';
import { fetchData, updateData, deleteData } from '../api';

export interface EnergyLogRecord {
  timestamp: number;
  solarPower: number;
  loadPower: number;
  gridPower: number;
  solarToLoad: number;
  solarToGrid: number;
  gridToLoad: number;
  batteryPower: number;
  curtailedPower: number;
}

export interface HistoricalDataResponse {
  records: EnergyLogRecord[];
  count: number;
  hours: number;
  startTime: number | null;
  endTime: number | null;
}

export interface DailyEnergyData {
  date: string;
  totalSolarWh: number;
  totalLoadWh: number;
  totalExportWh: number;
  totalImportWh: number;
  selfConsumptionWh: number;
  selfConsumptionPercent: number;
}

export interface StorageDevice {
  path: string;
  label: string;
  totalMB: number;
  freeMB: number;
  isRemovable: boolean;
}

export interface DatabaseSettings {
  loggingInterval: 1 | 5 | 15;
  storagePath: string;
  maxStorageMB: number;
  retentionDays: number;
  loggingEnabled: boolean;
}

export interface StorageStatus {
  usedMB: number;
  maxMB: number;
  usedPercent: number;
  fileCount: number;
  oldestFile: string | null;
  newestFile: string | null;
}

/**
 * Hook for fetching historical power data
 * @param hoursOrDate - Number of hours (for recent data) or date string 'YYYY-MM-DD' (for specific day)
 * @param pollingInterval - Polling interval in ms (only used when fetching recent data, not historical dates)
 */
export function useHistoricalData(hoursOrDate: number | string = 24, pollingInterval: number = 60000) {
  const [data, setData] = useState<EnergyLogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const isDateQuery = typeof hoursOrDate === 'string';

  const fetchHistoricalData = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = isDateQuery
        ? `/history?date=${hoursOrDate}`
        : `/history?hours=${hoursOrDate}`;
      const result: HistoricalDataResponse = await fetchData(endpoint);
      setData(result.records || []);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [hoursOrDate, isDateQuery]);

  useEffect(() => {
    fetchHistoricalData();
    // Only poll for recent data, not historical dates
    if (!isDateQuery) {
      const interval = setInterval(fetchHistoricalData, pollingInterval);
      return () => clearInterval(interval);
    }
  }, [fetchHistoricalData, pollingInterval, isDateQuery]);

  return { data, loading, error, lastUpdated, refetch: fetchHistoricalData };
}

/**
 * Hook for fetching available history dates
 */
export function useHistoryDates() {
  const [dates, setDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDates = useCallback(async () => {
    try {
      const result = await fetchData('/history/dates');
      setDates(result.dates || []);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDates();
  }, [fetchDates]);

  return { dates, loading, error, refetch: fetchDates };
}

/**
 * Hook for fetching daily energy totals
 */
export function useDailyEnergy(date?: string) {
  const [data, setData] = useState<DailyEnergyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchDailyEnergy = useCallback(async () => {
    try {
      const endpoint = date ? `/history/energy?date=${date}` : '/history/energy';
      const result: DailyEnergyData = await fetchData(endpoint);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchDailyEnergy();
  }, [fetchDailyEnergy]);

  return { data, loading, error, refetch: fetchDailyEnergy };
}

/**
 * Hook for database settings management
 */
export function useDatabaseSettings() {
  const [settings, setSettings] = useState<DatabaseSettings | null>(null);
  const [devices, setDevices] = useState<StorageDevice[]>([]);
  const [storageStatus, setStorageStatus] = useState<StorageStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      const result: DatabaseSettings = await fetchData('/settings/database');
      setSettings(result);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      console.log('[useDatabaseSettings] Fetching storage devices...');
      const result = await fetchData('/storage/devices');
      console.log('[useDatabaseSettings] Storage devices result:', result);
      setDevices(result.devices || []);
    } catch (err) {
      console.error('[useDatabaseSettings] Failed to fetch storage devices:', err);
    }
  }, []);

  const fetchStorageStatus = useCallback(async () => {
    try {
      const result: StorageStatus = await fetchData('/storage/status');
      setStorageStatus(result);
    } catch (err) {
      console.error('Failed to fetch storage status:', err);
    }
  }, []);

  const saveSettings = useCallback(async (newSettings: DatabaseSettings): Promise<boolean> => {
    setSaving(true);
    console.log('[useDatabaseSettings] Saving settings:', JSON.stringify(newSettings));
    try {
      const response = await updateData('/settings/database', newSettings);
      console.log('[useDatabaseSettings] Save response:', response.status, response.data);
      setSettings(newSettings);
      // Also update dataLogger on backend is handled by the API
      return true;
    } catch (err: any) {
      console.error('[useDatabaseSettings] Failed to save database settings:', err);
      console.error('[useDatabaseSettings] Error details:', err.response?.status, err.response?.data);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const clearLogs = useCallback(async (): Promise<boolean> => {
    try {
      await deleteData('/storage/logs');
      fetchStorageStatus();
      return true;
    } catch (err) {
      console.error('Failed to clear logs:', err);
      return false;
    }
  }, [fetchStorageStatus]);

  useEffect(() => {
    fetchSettings();
    fetchDevices();
    fetchStorageStatus();
  }, [fetchSettings, fetchDevices, fetchStorageStatus]);

  return {
    settings,
    devices,
    storageStatus,
    loading,
    saving,
    error,
    saveSettings,
    clearLogs,
    refetchDevices: fetchDevices,
    refetchStatus: fetchStorageStatus,
    refetch: fetchSettings,
  };
}
